import { Component, inject, Input } from '@angular/core'
import { CardComponent } from '@ui/Components/Card/Card.Component'
import { CardContentComponent } from '@ui/Components/Card/CardContent.Component'
import Order from '@client/Order'
import { CardTitleComponent } from '@ui/Components/Card/CardTitle.Component'
import { RaisedButtonComponent } from '@ui/Components/Form/Button/RaisedButton.Component'
import { BillingService } from '@client/Services/BillingService'
import HttpError, { FormErrors } from '@ui/HttpError'
import { SlideToggleComponent } from '@ui/Components/Form/SlideToggle/SlideToggle.Component'
import { TextInputComponent } from '@ui/Components/Form/TextInput/TextInput.Component'
import { SystemConfigService } from '@client/Services/SystemConfigService'
import { NgStyle } from '@angular/common'
import {
	FormValidationComponent,
	ValidationMessages,
} from '@ui/Components/Form/FormValidation.Component'
import { ReactiveFormsModule, Validators } from '@angular/forms'
import { CustomValidators } from '@ui/Validators/CustomValidators'
import { BaseCurrencyComponent } from '@ui/Components/Currency/BaseCurrency.Component'

@Component({
	selector: 'create-mandate',
	templateUrl: './CreateMandate.Component.html',
	imports: [
		CardComponent,
		CardContentComponent,
		CardTitleComponent,
		RaisedButtonComponent,
		SlideToggleComponent,
		TextInputComponent,
		NgStyle,
		ReactiveFormsModule,
		BaseCurrencyComponent,
	],
})
export class CreateMandateComponent extends FormValidationComponent {
	@Input() protected readonly order: Order

	public formErrors: FormErrors = {
		include_initial_payment: '',
		initial_payment: '',
	}

	protected validationMessages: ValidationMessages = {
		include_initial_payment: {
			required: 'You must specify if you want to include an initial payment.',
		},
		initial_payment: {
			required: 'Initial amount is required.',
			invalidCurrency: 'Initial amount must be a valid currency amount.',
		},
	}

	protected loading: boolean = false

	protected initialPaymentRaw: number = 0

	protected readonly systemConfigService: SystemConfigService

	private readonly billingService: BillingService

	constructor() {
		super()
		this.billingService = inject(BillingService)
		this.systemConfigService = inject(SystemConfigService)
	}

	protected readonly createMandate = async () => {
		if (this.loading) {
			return
		}

		this.loading = true

		try {
			this.validateForm()

			if (!this.form.valid) {
				this.loading = false
				return
			}

			const mandate = await this.billingService.createMandateRequest(
				this.order.id,
				`${window.location.origin}/my-account/orders/${this.order.id}/payment-plans/setup/2`,
				`${window.location.origin}/my-account/orders/${this.order.id}/payment-plans/setup/failed`,
				this.form.controls.include_initial_payment.value,
				this.initialPaymentRaw,
			)

			window.open(mandate.auth_url, '_self')
		} catch (error) {
			if (error instanceof HttpError) {
				if (error.isUnprocessableEntity()) {
					this.formErrors = error.handleFormErrors(this.formErrors, '')

					this.markInvalidFields()
				}
			} else {
				throw error
			}
		}

		this.loading = false
	}

	protected buildForm = () => {
		this.form = this.formBuilder.group({
			include_initial_payment: [true, [Validators.required]],
			initial_payment: ['', [CustomValidators.currencyValidator]],
		})

		this.form.valueChanges.subscribe((value) => {
			this.initialPaymentRaw =
				(parseFloat(value.initial_payment) || 0) *
				10 ** this.order.currency_precision

			if (!value.include_initial_payment) {
				this.initialPaymentRaw = 0
			}
		})
	}
}
